import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { ROUTER_LINKS } from '../common/consts/app-keys.const';
import { API_ORIGIN, SITE_ORIGIN } from '../../config/site';

// ─── Types ────────────────────────────────────────────────────────────────────

type Language = 'curl' | 'python' | 'javascript' | 'php' | 'ruby' | 'go';

const LANG_LABELS: Record<Language, string> = {
  curl: 'cURL',
  python: 'Python',
  javascript: 'JavaScript',
  php: 'PHP',
  ruby: 'Ruby',
  go: 'Go'
};

const BASE_URL = `${API_ORIGIN}/api/v1`;

// ─── Small helpers ────────────────────────────────────────────────────────────

const METHOD_COLORS: Record<'GET' | 'POST' | 'PATCH' | 'DELETE', string> = {
  GET: 'bg-[#dcfce7] text-[#166534] dark:bg-[#166534]/20 dark:text-[#4ade80]',
  POST: 'bg-[#cffafe] text-[#0e7490] dark:bg-[#0e7490]/20 dark:text-[#22d3ee]',
  PATCH: 'bg-[#ffedd5] text-[#9a3412] dark:bg-[#9a3412]/20 dark:text-[#fb923c]',
  DELETE: 'bg-[#fee2e2] text-[#991b1b] dark:bg-[#991b1b]/20 dark:text-[#f87171]'
};

const MethodBadge = ({ method }: { method: 'GET' | 'POST' | 'PATCH' | 'DELETE' }) => (
  <span className={`inline-flex items-center px-[8px] py-[2px] rounded-[5px] font-mono text-[11px] font-bold uppercase ${METHOD_COLORS[method]}`}>
    {method}
  </span>
);


const SectionAnchor = ({ id }: { id: string }) => (
  <span id={id} className="block scroll-mt-[230px] md:scroll-mt-[210px]" />
);

// inline code chip — matches docs Code component
const Chip = ({ children }: { children: React.ReactNode }) => (
  <code className="font-mono text-[12px] bg-[#F5F5F6] dark:bg-[#28282B] border border-[#E5E5ED] dark:border-[rgba(255,255,255,0.1)] rounded-[4px] px-[5px] py-[1px] text-base-black dark:text-white">
    {children}
  </code>
);

// ─── Code block with language tabs ───────────────────────────────────────────

interface CodeExample {
  curl: string;
  python: string;
  javascript: string;
  php: string;
  ruby: string;
  go: string;
}

const CodeBlock = ({ examples }: { examples: CodeExample }) => {
  const [activeLang, setActiveLang] = React.useState<Language>('curl');

  return (
    <div className="mt-[12px] rounded-[12px] overflow-hidden border border-[#2a2a2a]">
      <div className="flex gap-[4px] px-[12px] pt-[10px] pb-[8px] bg-[#161618]">
        {(Object.keys(LANG_LABELS) as Language[]).map((lang) => (
          <button
            key={lang}
            type="button"
            onClick={() => setActiveLang(lang)}
            className={`px-[10px] py-[3px] rounded-[16px] font-body text-[11px] font-medium transition-all ${
              activeLang === lang
                ? 'bg-[#0ea5c9] text-white'
                : 'bg-[#2a2a2c] text-[#818185] hover:text-white'
            }`}
          >
            {LANG_LABELS[lang]}
          </button>
        ))}
      </div>
      <div className="bg-[#1D1D1E] px-[16px] py-[14px] overflow-x-auto">
        <pre className="font-mono text-[12px] text-[#e5e5e5] leading-relaxed whitespace-pre">
          {examples[activeLang]}
        </pre>
      </div>
    </div>
  );
};

// ─── Endpoint row ─────────────────────────────────────────────────────────────

interface EndpointProps {
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  description: string;
  examples: CodeExample;
}

const Endpoint = ({ method, path, description, examples }: EndpointProps) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)] rounded-[12px] overflow-hidden mb-[10px]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-[12px] px-[16px] py-[12px] bg-[#F8F8F9] dark:bg-[#1C1C1F] hover:bg-[#F2F2F4] dark:hover:bg-[#222226] transition-colors text-left"
      >
        <MethodBadge method={method} />
        <code className="font-mono text-[13px] text-base-black dark:text-white flex-1">{path}</code>
        <span className="font-body text-[13px] text-[#818185] dark:text-[#6B6B6F] hidden md:block">{description}</span>
        <svg
          width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          className={`shrink-0 text-[#818185] dark:text-[#6B6B6F] transition-transform ${open ? 'rotate-180' : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div className="px-[16px] pb-[16px] bg-[#F8F8F9] dark:bg-[#1C1C1F] border-t border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)]">
          <p className="font-body text-[13px] text-[#374151] dark:text-[#D1D1D4] mt-[12px] md:hidden">{description}</p>
          <CodeBlock examples={examples} />
        </div>
      )}
    </div>
  );
};

// ─── Sidebar TOC ──────────────────────────────────────────────────────────────

const SECTION_IDS = ['overview', 'authentication', 'queues', 'participants', 'teams', 'webhooks', 'api-keys'] as const;

const Sidebar = ({ activeId, onSelect }: { activeId: string; onSelect: (id: string) => void }) => {
  const { t } = useTranslation();
  const sectionKey: Record<string, string> = {
    'overview': 'overview', 'authentication': 'authentication', 'queues': 'queues',
    'participants': 'participants', 'teams': 'teams', 'webhooks': 'webhooks', 'api-keys': 'apiKeys'
  };
  return (
    <nav className="hidden laptop:flex flex-col gap-[2px] sticky top-[190px] self-start w-[200px] shrink-0">
      <p className="font-mono text-[10px] tracking-[0.08em] uppercase text-[#818185] dark:text-[#6B6B6F] font-bold px-[10px] mb-[12px]">
        {t('developer.contents')}
      </p>
      {SECTION_IDS.map((id) => (
        <a
          key={id}
          href={`#${id}`}
          className={`px-[10px] py-[6px] rounded-[6px] font-body text-[13px] transition-all border-r-[2px] ${
            activeId === id
              ? 'text-base-black dark:text-white font-semibold bg-[#F5F5F6] dark:bg-[#28282B] border-r-[#1D1D1E] dark:border-r-white'
              : 'text-[#818185] dark:text-[#A0A0A3] hover:text-base-black dark:hover:text-white hover:bg-[#F7F7F8] dark:hover:bg-[#28282B] border-r-transparent'
          }`}
          onClick={(e) => {
            e.preventDefault();
            onSelect(id);
            document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        >
          {t(`developer.sections.${sectionKey[id]}`)}
        </a>
      ))}
    </nav>
  );
};

// ─── Code Examples ────────────────────────────────────────────────────────────

const makeExamples = (
  method: string,
  path: string,
  body?: string
): CodeExample => {
  const url = `${BASE_URL}${path}`;
  const bodyJson = body ? JSON.stringify(JSON.parse(body), null, 2) : null;

  return {
    curl: [
      `curl -X ${method} ${url} \\`,
      `  -H "Authorization: Bearer YOUR_API_KEY"`,
      ...(bodyJson ? [`  -H "Content-Type: application/json" \\`, `  -d '${body}'`] : [])
    ].join('\n'),

    python: [
      `import requests`,
      ``,
      `response = requests.${method.toLowerCase()}(`,
      `    "${url}",`,
      `    headers={"Authorization": "Bearer YOUR_API_KEY"}${bodyJson ? `,` : ''}`,
      ...(bodyJson ? [`    json=${bodyJson.replace(/\n/g, '\n    ')}`] : []),
      `)`,
      `data = response.json()`
    ].join('\n'),

    javascript: [
      `const response = await fetch(`,
      `  "${url}",`,
      `  {`,
      `    method: "${method}",`,
      `    headers: {`,
      `      "Authorization": "Bearer YOUR_API_KEY"${bodyJson ? ',' : ''}`,
      ...(bodyJson ? [`      "Content-Type": "application/json"`] : []),
      `    }${bodyJson ? ',' : ''}`,
      ...(bodyJson ? [`    body: JSON.stringify(${body})`] : []),
      `  }`,
      `);`,
      `const data = await response.json();`
    ].join('\n'),

    php: [
      `$ch = curl_init("${url}");`,
      `curl_setopt($ch, CURLOPT_CUSTOMREQUEST, "${method}");`,
      `curl_setopt($ch, CURLOPT_HTTPHEADER, [`,
      `    "Authorization: Bearer YOUR_API_KEY"${bodyJson ? ',' : ''}`,
      ...(bodyJson ? [`    "Content-Type: application/json"`] : []),
      `]);`,
      `curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);`,
      ...(bodyJson ? [`curl_setopt($ch, CURLOPT_POSTFIELDS, '${body}');`] : []),
      `$data = json_decode(curl_exec($ch), true);`
    ].join('\n'),

    ruby: [
      `require "net/http"`,
      `require "json"`,
      ``,
      `uri = URI("${url}")`,
      `http = Net::HTTP.new(uri.host, uri.port)`,
      `http.use_ssl = true`,
      `request = Net::HTTP::${method.charAt(0) + method.slice(1).toLowerCase()}`,
      `  .new(uri)`,
      `request["Authorization"] = "Bearer YOUR_API_KEY"`,
      ...(bodyJson ? [`request["Content-Type"] = "application/json"`, `request.body = '${body}'`] : []),
      `response = JSON.parse(http.request(request).body)`
    ].join('\n'),

    go: [
      ...(bodyJson
        ? [
            `import "bytes"`,
            `body := bytes.NewBufferString(\`${body}\`)`,
            `req, _ := http.NewRequest("${method}", "${url}", body)`,
            `req.Header.Set("Content-Type", "application/json")`
          ]
        : [`req, _ := http.NewRequest("${method}", "${url}", nil)`]),
      `req.Header.Set("Authorization", "Bearer YOUR_API_KEY")`,
      `resp, _ := http.DefaultClient.Do(req)`,
      `defer resp.Body.Close()`
    ].join('\n')
  };
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const DeveloperPage = () => {
  const { t } = useTranslation();
  const settingsLink = `${ROUTER_LINKS.SETTINGS}?section=developer`;
  const [activeId, setActiveId] = React.useState(
    () => window.location.hash.replace('#', '') || 'overview'
  );
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
  const manualScrollRef = React.useRef(false);

  const handleSelect = (id: string) => {
    manualScrollRef.current = true;
    setActiveId(id);
    window.history.replaceState(null, '', `#${id}`);
    setTimeout(() => { manualScrollRef.current = false; }, 800);
  };

  React.useEffect(() => {
    const handleScroll = () => {
      if (manualScrollRef.current) return;
      const viewportHeight = window.innerHeight;
      let currentId = SECTIONS[0].id;
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= viewportHeight * 0.4) currentId = s.id;
      }
      if (currentId !== activeId) setActiveId(currentId);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeId]);

  return (
    <div className="max-w-[1000px] mx-auto py-[32px] md:py-[48px]">
      {/* Sticky header — locks to top while content scrolls, same pattern as docs page */}
      <div className="sticky top-[60px] md:top-[70px] z-20 bg-white dark:bg-[#121214] pb-[12px] md:pb-[16px] -mx-[16px] px-[16px] md:mx-0 md:px-0">
        <h1 className="font-display text-[28px] md:text-[36px] font-bold text-base-black dark:text-white tracking-[-0.5px] mb-[8px]">
          {t('developer.title')}
        </h1>
        <p className="font-body text-[16px] text-[#818185] dark:text-[#6B6B6F] mb-[12px] md:mb-[16px]">
          {t('developer.subtitle')}
        </p>

        {/* Mobile TOC toggle — hidden on laptop+ where the sidebar is visible */}
        <div className="laptop:hidden relative">
          <button
            type="button"
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="h-[36px] px-[14px] rounded-[6px] bg-[#F5F5F6] dark:bg-[#28282B] border border-[#E5E5ED] dark:border-[rgba(255,255,255,0.08)] font-body text-[13px] font-medium text-base-black dark:text-white flex items-center gap-[6px]"
          >
            {mobileNavOpen ? t('developer.hideContents') : t('developer.showContents')}
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none" className={`transition-transform ${mobileNavOpen ? 'rotate-180' : ''}`}>
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {mobileNavOpen && (
            <div className="absolute top-[calc(100%+6px)] left-0 right-0 bg-white dark:bg-[#1C1C1F] rounded-[12px] border border-[rgba(0,0,0,0.11)] dark:border-[rgba(255,255,255,0.08)] shadow-[0_4px_20px_rgba(0,0,0,0.12)] py-[6px] max-h-[50vh] overflow-y-auto z-30">
              <p className="font-mono text-[10px] tracking-[0.08em] uppercase text-[#818185] dark:text-[#6B6B6F] font-bold px-[14px] py-[6px]">
                {t('developer.contents')}
              </p>
              {SECTION_IDS.map((id) => {
                const sectionKey: Record<string, string> = {
                  'overview': 'overview', 'authentication': 'authentication', 'queues': 'queues',
                  'participants': 'participants', 'teams': 'teams', 'webhooks': 'webhooks', 'api-keys': 'apiKeys'
                };
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      handleSelect(id);
                      setMobileNavOpen(false);
                      setTimeout(() => {
                        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 50);
                    }}
                    className={`w-full text-left px-[14px] py-[8px] font-body text-[13px] transition-colors ${
                      activeId === id
                        ? 'text-base-black dark:text-white font-semibold bg-[#F5F5F6] dark:bg-[#28282B]'
                        : 'text-[#818185] dark:text-[#A0A0A3] hover:text-base-black dark:hover:text-white hover:bg-[#F7F7F8] dark:hover:bg-[#28282B]'
                    }`}
                  >
                    {t(`developer.sections.${sectionKey[id]}`)}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-[40px] items-start">
        <Sidebar activeId={activeId} onSelect={handleSelect} />

        <div className="flex-1 min-w-0 flex flex-col gap-[48px]">
          <section>
            <SectionAnchor id="overview" />
            <h2 className="font-display text-[24px] font-bold text-base-black dark:text-white tracking-[-0.4px] mb-[8px]">
              {t('developer.sections.overview')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] dark:text-[#D1D1D4] mb-[16px]">
              {t('developer.overview.description')}
            </p>
            <div className="bg-white dark:bg-[#1C1C1F] border border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)] rounded-[16px] p-[22px] flex flex-col gap-[16px]">
              <div>
                <p className="font-body text-[11px] font-semibold uppercase tracking-wider text-[#818185] dark:text-[#6B6B6F] mb-[4px]">
                  {t('developer.overview.baseUrl')}
                </p>
                <code className="font-mono text-[13px] bg-[#F5F5F6] dark:bg-[#28282B] border border-[#E5E5ED] dark:border-[rgba(255,255,255,0.1)] rounded-[8px] px-[12px] py-[8px] block text-base-black dark:text-white">
                  {BASE_URL}
                </code>
              </div>
              <div>
                <p className="font-body text-[11px] font-semibold uppercase tracking-wider text-[#818185] dark:text-[#6B6B6F] mb-[4px]">
                  {t('developer.overview.rateLimits')}
                </p>
                <p className="font-body text-[13px] text-[#374151] dark:text-[#D1D1D4]">
                  <Trans i18nKey="developer.overview.rateLimitsDesc" components={{ chip: <Chip /> }} />
                </p>
              </div>
              <div>
                <p className="font-body text-[11px] font-semibold uppercase tracking-wider text-[#818185] dark:text-[#6B6B6F] mb-[4px]">
                  {t('developer.overview.responseFormat')}
                </p>
                <p className="font-body text-[13px] text-[#374151] dark:text-[#D1D1D4]">
                  <Trans i18nKey="developer.overview.responseFormatDesc" components={{ chip: <Chip /> }} />
                </p>
              </div>
            </div>
          </section>

          <section>
            <SectionAnchor id="authentication" />
            <h2 className="font-display text-[24px] font-bold text-base-black dark:text-white tracking-[-0.4px] mb-[8px]">
              {t('developer.sections.authentication')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] dark:text-[#D1D1D4] mb-[16px]">
              {t('developer.auth.description')}
            </p>
            <div className="flex flex-col gap-[16px]">
              <div className="bg-white dark:bg-[#1C1C1F] border border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)] rounded-[16px] p-[22px]">
                <h3 className="font-body text-[16px] font-semibold text-base-black dark:text-white mb-[6px]">
                  {t('developer.auth.apiKeys.title')}
                </h3>
                <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[12px]">
                  {t('developer.auth.apiKeys.bestFor')}
                </p>
                <p className="font-body text-[14px] text-[#374151] dark:text-[#D1D1D4] mb-[12px]">
                  <Trans
                    i18nKey="developer.auth.apiKeys.description"
                    components={{ link: <Link to={settingsLink} className="text-[#0ea5c9] hover:underline" /> }}
                  />
                </p>
                <p className="font-body text-[11px] font-semibold uppercase tracking-wider text-[#818185] dark:text-[#6B6B6F] mb-[8px]">
                  {t('developer.auth.apiKeys.exampleRequest')}
                </p>
                <CodeBlock examples={makeExamples('GET', '/developer/queues')} />
              </div>

              <div className="bg-white dark:bg-[#1C1C1F] border border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)] rounded-[16px] p-[22px] flex flex-col gap-[16px]">
                <div>
                  <h3 className="font-body text-[16px] font-semibold text-base-black dark:text-white mb-[6px]">
                    {t('developer.auth.oauth.title')}
                  </h3>
                  <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[12px]">
                    {t('developer.auth.oauth.bestFor')}
                  </p>
                  <p className="font-body text-[13px] text-[#374151] dark:text-[#D1D1D4] mb-[10px]">
                    {t('developer.auth.oauth.description1')}
                  </p>
                  <p className="font-body text-[13px] text-[#374151] dark:text-[#D1D1D4]">
                    <Trans
                      i18nKey="developer.auth.oauth.description2"
                      components={{
                        link: <Link to={settingsLink} className="text-[#0ea5c9] hover:underline" />,
                        chip1: <Chip />,
                        chip2: <Chip />
                      }}
                    />
                  </p>
                </div>

                <div>
                  <p className="font-body text-[13px] font-semibold text-base-black dark:text-white mb-[4px]">
                    {t('developer.auth.oauth.step1Title')}
                  </p>
                  <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[8px]">
                    <Trans i18nKey="developer.auth.oauth.step1Desc" components={{ chip: <Chip /> }} />
                  </p>
                  <div className="bg-[#F5F5F6] dark:bg-[#28282B] border border-[#E5E5ED] dark:border-[rgba(255,255,255,0.1)] rounded-[8px] px-[12px] py-[10px]">
                    <pre className="font-mono text-[12px] text-base-black dark:text-white whitespace-pre-wrap break-all">
{`${SITE_ORIGIN}/oauth/authorize
  ?response_type=code
  &client_id=YOUR_CLIENT_ID
  &redirect_uri=https://your-app.com/callback
  &state=RANDOM_STATE_STRING`}
                    </pre>
                  </div>
                </div>

                <div>
                  <p className="font-body text-[13px] font-semibold text-base-black dark:text-white mb-[4px]">
                    {t('developer.auth.oauth.step2Title')}
                  </p>
                  <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[8px]">
                    <Trans
                      i18nKey="developer.auth.oauth.step2Desc"
                      components={{ chip1: <Chip />, chip2: <Chip />, chip3: <Chip /> }}
                    />
                  </p>
                  <CodeBlock examples={{
                    curl: `curl -X POST ${BASE_URL}/oauth/token \\
  -H "Content-Type: application/json" \\
  -d '{
    "grant_type": "authorization_code",
    "code": "AUTH_CODE",
    "client_id": "YOUR_CLIENT_ID",
    "client_secret": "YOUR_CLIENT_SECRET",
    "redirect_uri": "https://your-app.com/callback"
  }'`,
                    python: `import requests

response = requests.post(
    "${BASE_URL}/oauth/token",
    json={
        "grant_type": "authorization_code",
        "code": "AUTH_CODE",
        "client_id": "YOUR_CLIENT_ID",
        "client_secret": "YOUR_CLIENT_SECRET",
        "redirect_uri": "https://your-app.com/callback"
    }
)
tokens = response.json()
access_token = tokens["access_token"]
refresh_token = tokens["refresh_token"]`,
                    javascript: `const response = await fetch("${BASE_URL}/oauth/token", {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    grant_type: "authorization_code",
    code: "AUTH_CODE",
    client_id: "YOUR_CLIENT_ID",
    client_secret: "YOUR_CLIENT_SECRET",
    redirect_uri: "https://your-app.com/callback"
  })
});
const { access_token, refresh_token } = await response.json();`,
                    php: `$response = json_decode(file_get_contents(
    "${BASE_URL}/oauth/token",
    false,
    stream_context_create([
        "http" => [
            "method"  => "POST",
            "header"  => "Content-Type: application/json",
            "content" => json_encode([
                "grant_type"    => "authorization_code",
                "code"          => "AUTH_CODE",
                "client_id"     => "YOUR_CLIENT_ID",
                "client_secret" => "YOUR_CLIENT_SECRET",
                "redirect_uri"  => "https://your-app.com/callback"
            ])
        ]
    ])
));
$access_token = $response->access_token;`,
                    ruby: `require "net/http"
require "json"

uri = URI("${BASE_URL}/oauth/token")
http = Net::HTTP.new(uri.host, uri.port)
http.use_ssl = true
req = Net::HTTP::Post.new(uri)
req["Content-Type"] = "application/json"
req.body = JSON.generate({
  grant_type: "authorization_code",
  code: "AUTH_CODE",
  client_id: "YOUR_CLIENT_ID",
  client_secret: "YOUR_CLIENT_SECRET",
  redirect_uri: "https://your-app.com/callback"
})
tokens = JSON.parse(http.request(req).body)
access_token = tokens["access_token"]`,
                    go: `body := bytes.NewBufferString(\`{
  "grant_type": "authorization_code",
  "code": "AUTH_CODE",
  "client_id": "YOUR_CLIENT_ID",
  "client_secret": "YOUR_CLIENT_SECRET",
  "redirect_uri": "https://your-app.com/callback"
}\`)
req, _ := http.NewRequest("POST", "${BASE_URL}/oauth/token", body)
req.Header.Set("Content-Type", "application/json")
resp, _ := http.DefaultClient.Do(req)
defer resp.Body.Close()`
                  }} />
                </div>

                <div>
                  <p className="font-body text-[13px] font-semibold text-base-black dark:text-white mb-[4px]">
                    {t('developer.auth.oauth.step3Title')}
                  </p>
                  <p className="font-body text-[13px] text-[#818185] dark:text-[#A0A0A3] mb-[8px]">
                    <Trans
                      i18nKey="developer.auth.oauth.step3Desc"
                      components={{ chip1: <Chip />, chip2: <Chip />, strong: <strong /> }}
                    />
                  </p>
                  <CodeBlock examples={makeExamples('GET', '/developer/queues')} />
                </div>
              </div>
            </div>
          </section>

          <section>
            <SectionAnchor id="queues" />
            <h2 className="font-display text-[24px] font-bold text-base-black dark:text-white tracking-[-0.4px] mb-[4px]">
              {t('developer.sections.queues')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] dark:text-[#D1D1D4] mb-[16px]">
              {t('developer.queues.description')}
            </p>
            <Endpoint method="GET" path="/developer/queues" description={t('developer.queues.listAll')} examples={makeExamples('GET', '/developer/queues')} />
            <Endpoint method="POST" path="/developer/queues" description={t('developer.queues.create')} examples={makeExamples('POST', '/developer/queues', '{"title":"Office Hours","meetingUrl":"https://zoom.us/j/123"}')} />
            <Endpoint method="GET" path="/developer/queues/:id" description={t('developer.queues.getOne')} examples={makeExamples('GET', '/developer/queues/42')} />
            <Endpoint method="PATCH" path="/developer/queues/:id" description={t('developer.queues.update')} examples={makeExamples('PATCH', '/developer/queues/42', '{"title":"Updated Title","isActive":true}')} />
            <Endpoint method="DELETE" path="/developer/queues/:id" description={t('developer.queues.close')} examples={makeExamples('DELETE', '/developer/queues/42')} />
          </section>

          <section>
            <SectionAnchor id="participants" />
            <h2 className="font-display text-[24px] font-bold text-base-black dark:text-white tracking-[-0.4px] mb-[4px]">
              {t('developer.sections.participants')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] dark:text-[#D1D1D4] mb-[16px]">
              {t('developer.participants.description')}
            </p>
            <Endpoint method="GET" path="/developer/queues/:id/participants" description={t('developer.participants.listAll')} examples={makeExamples('GET', '/developer/queues/42/participants')} />
            <Endpoint method="POST" path="/developer/queues/:id/participants" description={t('developer.participants.add')} examples={makeExamples('POST', '/developer/queues/42/participants', '{"name":"Alice Smith"}')} />
            <Endpoint method="POST" path="/developer/queues/:id/next" description={t('developer.participants.callNext')} examples={makeExamples('POST', '/developer/queues/42/next')} />
            <Endpoint method="DELETE" path="/developer/queues/:id/participants/:participantId" description={t('developer.participants.remove')} examples={makeExamples('DELETE', '/developer/queues/42/participants/7')} />
          </section>

          <section>
            <SectionAnchor id="teams" />
            <h2 className="font-display text-[24px] font-bold text-base-black dark:text-white tracking-[-0.4px] mb-[4px]">
              {t('developer.sections.teams')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] dark:text-[#D1D1D4] mb-[16px]">
              {t('developer.teams.description')}
            </p>
            <Endpoint method="GET" path="/developer/teams" description={t('developer.teams.listAll')} examples={makeExamples('GET', '/developer/teams')} />
            <Endpoint method="GET" path="/developer/teams/:id" description={t('developer.teams.getOne')} examples={makeExamples('GET', '/developer/teams/5')} />
            <Endpoint method="GET" path="/developer/teams/:id/queues" description={t('developer.teams.getQueues')} examples={makeExamples('GET', '/developer/teams/5/queues')} />
          </section>

          <section>
            <SectionAnchor id="webhooks" />
            <h2 className="font-display text-[24px] font-bold text-base-black dark:text-white tracking-[-0.4px] mb-[4px]">
              {t('developer.sections.webhooks')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] dark:text-[#D1D1D4] mb-[16px]">
              {t('developer.webhooks.description')}
            </p>
            <Endpoint method="GET" path="/developer/webhooks" description={t('developer.webhooks.listAll')} examples={makeExamples('GET', '/developer/webhooks')} />
            <Endpoint method="POST" path="/developer/webhooks" description={t('developer.webhooks.create')} examples={makeExamples('POST', '/developer/webhooks', '{"name":"My Discord","url":"https://discord.com/api/webhooks/...","provider":"discord"}')} />
            <Endpoint method="DELETE" path="/developer/webhooks/:id" description={t('developer.webhooks.delete')} examples={makeExamples('DELETE', '/developer/webhooks/3')} />
          </section>

          <section>
            <SectionAnchor id="api-keys" />
            <h2 className="font-display text-[24px] font-bold text-base-black dark:text-white tracking-[-0.4px] mb-[4px]">
              {t('developer.sections.apiKeys')}
            </h2>
            <p className="font-body text-[14px] text-[#374151] dark:text-[#D1D1D4] mb-[16px]">
              {t('developer.apiKeysSection.description')}
            </p>
            <div className="bg-white dark:bg-[#1C1C1F] border border-[rgba(0,0,0,0.07)] dark:border-[rgba(255,255,255,0.06)] rounded-[16px] p-[22px] flex flex-col sm:flex-row sm:items-center gap-[16px]">
              <div className="flex items-center gap-[14px] flex-1 min-w-0">
                <div className="w-[40px] h-[40px] rounded-[10px] bg-[#F5F5F6] dark:bg-[#28282B] flex items-center justify-center shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#0ea5c9]">
                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
                  </svg>
                </div>
                <div>
                  <p className="font-body text-[14px] font-semibold text-base-black dark:text-white">
                    {t('developer.apiKeysSection.manageTitle')}
                  </p>
                  <p className="font-body text-[13px] text-[#818185] dark:text-[#6B6B6F]">
                    {t('developer.apiKeysSection.manageDesc')}
                  </p>
                </div>
              </div>
              <Link
                to={settingsLink}
                className="shrink-0 h-[38px] px-[20px] flex items-center bg-[#1D1D1E] dark:bg-[#C8C8CB] text-white dark:text-[#1C1C1F] rounded-[32px] font-body text-[13px] font-medium hover:bg-[#2E2E30] dark:hover:bg-[#D8D8DB] transition-all"
              >
                {t('developer.apiKeysSection.openSettings')}
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DeveloperPage;
